import { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import { apiClient, clearApiCache } from '../api/axios';

// Align with Django User model
interface UserProfile {
  id: number;
  email: string;
  role: 'STUDENT' | 'HOD' | 'ADMIN' | 'COORDINATOR' | 'ASSISTANT';
  branch: string | null;
  is_2fa_enabled: boolean;
  /**
   * True for HODs (and other non-coordinator roles) who additionally act
   * as Internship Coordinators. Lets them access /cell/drives without
   * changing their primary role. Backend honors this in permissions.py.
   */
  is_internship_coordinator: boolean;
}

interface AuthContextType {
  user: UserProfile | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (access: string, refresh: string) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<UserProfile | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchUser = async () => {
    try {
      // Django UserRole stores lowercase ("student"/"hod"/"admin") —
      // frontend route guards compare against uppercase, so normalize here
      // rather than sprinkle .toUpperCase() across 10 files.
      const response = await apiClient.get<UserProfile>('/auth/me/');
      const raw = response.data;
      setUser({
        ...raw,
        role: raw.role.toString().toUpperCase() as UserProfile['role'],
      });
    } catch {
      logout();
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem('access_token');
    if (token) {
      fetchUser();
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = (access: string, refresh: string) => {
    // Clear any stale cache from a previous session BEFORE fetching the
    // new user's profile. The login POST goes through the bare `axios`
    // instance (not apiClient), so the mutation interceptor that normally
    // busts the cache never fires — we must do it manually here.
    // Without this, `fetchUser()` below can get a cache hit on
    // `/auth/me/` with the previous user's role and show the wrong dashboard.
    clearApiCache();
    localStorage.setItem('access_token', access);
    localStorage.setItem('refresh_token', refresh);
    setIsLoading(true);
    fetchUser();
  };

  const logout = () => {
    // 1. Read refresh token before wiping storage — needed for blacklist call.
    const refreshToken = localStorage.getItem('refresh_token');

    // 2. Wipe the API response cache FIRST so no stale /auth/me/ data
    //    survives into the next login. Also broadcasts a bust to sibling
    //    tabs so they clear too.
    clearApiCache();

    // 3. Remove tokens from storage and clear React state immediately —
    //    the user is logged out on this device regardless of what happens
    //    on the server.
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    setUser(null);

    // 4. Best-effort server-side blacklist so the old refresh token can't
    //    be replayed from another device. Fire-and-forget — if the network
    //    call fails the token expires naturally at its DB TTL.
    if (refreshToken) {
      apiClient
        .post('/auth/token/blacklist/', { refresh: refreshToken })
        .catch(() => {
          // Ignore — the user is already logged out locally.
        });
    }
  };

  const value = {
    user,
    isAuthenticated: !!user,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// eslint-disable-next-line react-refresh/only-export-components
export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
